import React, { useState, useEffect } from "react";
import {
  TextField,
  IconButton,
  InputAdornment,
  Skeleton,
  Box,
  Select,
  MenuItem,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface CustomSearchBarProps {
  onSearch: (searchTerm: string) => void;
  placeholder?: string;
}

const ProductSearchPanel: React.FC<CustomSearchBarProps> = ({
  onSearch,
  placeholder = "Search by products...",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true); // New state for loading

  // Simulate data loading delay
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 300);
  }, []);

  const handleSearch = () => {
    onSearch(searchTerm);
  };

  const handleClear = () => {
    setSearchTerm("");
    onSearch("");
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(90deg, #1c1c1c, #2c2c2c)",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        padding: {
          xs: "12px 5px", // mobile
          sm: '12px 5px', // tablet
          md: '6px 20px', // small laptop
          lg: "6px 20px", // desktop
        },
      }}
    >
      {/* Category Dropdown */}
      <Box
        sx={{
          display: {
            xs: "none",
            sm: "none",
            md: "block",
            lg: "block",
            xl: "block",
          },
        }}
      >
        <Select
          defaultValue="all"
          variant="standard"
          disableUnderline
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            color: "#fff",
            minWidth: "140px",
            "& .MuiSelect-icon": { color: "#fff" },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: "#000",
                color: "#fff",
                "& .MuiMenuItem-root": {
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                  },
                },
              },
            },
          }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          <MenuItem value="electronics">Electronics</MenuItem>
          <MenuItem value="fashion">Fashion</MenuItem>
          <MenuItem value="grocery">Grocery</MenuItem>
        </Select>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ mx: 2, backgroundColor: "#555" }}
        />
      </Box>

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          height={56}
          animation="wave"
          sx={{ borderRadius: "18px" }}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          sx={{
            width: "100%",
            // bgcolor: "#eff1f5",
            //background: "linear-gradient(90deg, #1c1c1c, #2c2c2c)",
            borderRadius: "8px",

            // Input text color
            "& .MuiOutlinedInput-input": {
              color: "#fff",
              padding: 0,
            },

            // Placeholder color
            "& .MuiOutlinedInput-input::placeholder": {
              color: "#ccc",
              opacity: 1, // Important (default MUI reduces opacity)
            },

            "& .MuiInputLabel-root": {
              color: "#fff",
            },
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "transparent",
                borderRadius: "8px",
              },
              "&:hover fieldset": {
                borderColor: "transparent",
              },
              "&.Mui-focused fieldset": {
                borderColor: "transparent",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: "-10px", mr: "-1px" }}>
                <IconButton
                  onClick={handleSearch}
                  aria-label="Search by products"
                  sx={{ color: "#ccc" }}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {searchTerm ? (
                  <IconButton onClick={handleClear} aria-label="clear">
                    <ClearIcon />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default ProductSearchPanel;
